import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  CodeBracketIcon,
  CubeTransparentIcon,
  ServerStackIcon,
  CloudIcon,
  ChartBarSquareIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { fadeInUp, fadeIn, slideInLeft, revealMask, viewportOnce } from '../../lib/animations';

const BlurUpPortrait: React.FC = () => {
  const fullSrc = new URL('../../../images/Cheech.JPG', import.meta.url).href;
  // For now we reuse same image as placeholder; could swap for an actual tiny base64 asset.
  const tinySrc = fullSrc;
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative w-full max-w-[320px] aspect-[4/5]">
      <div
        aria-hidden
        className={`absolute inset-0 rounded-xl overflow-hidden transition filter duration-700 ${loaded ? 'opacity-0 scale-100' : 'opacity-100 scale-[1.02] blur-xl'}`}
        style={{ backgroundImage: `url(${tinySrc})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      />
      <img
        src={fullSrc}
        alt="Christopher Bachicha"
        loading="lazy"
        width={320}
        height={400}
        onLoad={() => setLoaded(true)}
        className={`relative rounded-xl object-cover w-full h-full border border-white/10 shadow-lg shadow-black/40 transition duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
};

export const Hero: React.FC = () => {
  const ref = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  // Subtle upward parallax (moves slower than scroll)
  const y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.85]);
  return (
    <section ref={ref} className="relative pt-24 pb-32 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_30%_20%,rgba(13,165,255,0.15),transparent_60%)]" />
      <div className="mx-auto max-w-6xl px-4 grid gap-12 md:grid-cols-[minmax(0,1fr)_340px] items-center">
        <div>
          <motion.h1
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={revealMask}
            className="text-balance text-5xl md:text-6xl font-bold tracking-tight text-gradient"
          >
            Engineering clarity, performance, and reliability into every release.
          </motion.h1>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeInUp}
            className="mt-8 max-w-3xl text-lg leading-relaxed text-white/70"
          >
            I&apos;m Christopher Bachicha — a Staff Software Engineer focused on shipping durable, human‑centered software systems at scale.
          </motion.p>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeInUp}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href="#story" className="btn-outline">My Story</a>
            <a href="#experiments" className="btn-outline">Experiments</a>
          </motion.div>
        </div>
        <motion.figure
          style={{ y, opacity }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={viewportOnce}
          transition={{ duration: 0.8, ease: [0.22,1,0.36,1] }}
          className="relative group md:justify-self-end"
        >
          <div className="absolute -inset-2 rounded-xl bg-gradient-to-tr from-brand-400/40 via-brand-300/20 to-transparent opacity-0 group-hover:opacity-100 blur-lg transition" />
          <BlurUpPortrait />
          <figcaption className="sr-only">Portrait of Christopher Bachicha</figcaption>
        </motion.figure>
      </div>
    </section>
  );
};

export const Story: React.FC = () => (
  <section id="story" className="py-28">
    <div className="mx-auto max-w-5xl px-4 grid gap-16 md:grid-cols-2 items-start">
      <motion.div variants={slideInLeft} initial="hidden" whileInView="visible" viewport={viewportOnce}>
        <h2 className="text-3xl font-semibold tracking-tight">Story & Impact</h2>
        <p className="mt-6 text-white/70 leading-relaxed">
          I&apos;m a Staff Software Engineer and hands-on leader with over 20 years of experience building and scaling SaaS platforms. My work centers on pragmatic modernization and performance improvements—from re‑platforming legacy UI stacks to hardening critical pipelines. I enjoy turning scattered systems into cohesive and observable architectures.
        </p>
        <p className="mt-4 text-white/60 leading-relaxed">
          Recent focus areas: a React 18 migration across dozens of applications; a product release yielding 3× faster page loads; and a hardened video pipeline processing 225,000 recordings a month with a near‑zero failure rate—<span className="font-bold">while reducing infrastructure spend</span>. I also prototyped and launched generative AI features that derive names and summaries from video transcripts.
        </p>
        <p className="mt-4 text-white/60 leading-relaxed">
          My approach favors measured changes with outsized impact: removing 139 TB of stale assets to reduce liability, accelerating CI to cut feedback loops in half, and coaching engineers into emerging leaders. I aim to give teams clarity, leverage data responsibly, and keep user outcomes central.
        </p>
      </motion.div>
      <ul
        className="space-y-6"
      >
        {[
          { title: 'Scale & Deliverability', desc: 'Video email systems sending millions monthly with high deliverability.' },
          { title: 'Reliability & Cost', desc: 'Video pipeline: 225k recordings/mo → near 0% failures & ~$18K annual savings.' },
          { title: 'Modernization', desc: 'Led migration of React 18 upgrades, converted to a SPA powering 3× faster page loads.' },
          { title: 'Privacy & Governance', desc: 'GDPR + retention tooling deleted 139 TB, saving $13.6K/yr & reducing risk.' },
          { title: 'Developer Velocity', desc: 'CI integration tests: 33 → 15 min (‑55%); success 47% → 90%; sprint commit 66% → 80%.' },
          { title: 'Product & AI', desc: 'Launched Prompt Video Email + AI title/summary features driving adoption.' },
          { title: 'Leadership & Mentorship', desc: 'Formed multiple technical teams; coached engineers into respected technical leaders.' },
  ].map(item => (
          <li
            key={item.title}
            className="rounded-lg border border-white/10 bg-white/5 backdrop-blur px-5 py-4 hover:border-brand-400/40 transition"
          >
            <h3 className="text-sm font-semibold tracking-wide text-brand-300">{item.title}</h3>
            <p className="mt-1 text-sm text-white/70 leading-relaxed">{item.desc}</p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export const Expertise: React.FC = () => (
  <section id="expertise" className="py-28 border-t border-white/5">
    <div className="mx-auto max-w-6xl px-4">
      <motion.h2 className="text-3xl font-semibold" variants={fadeIn} initial="hidden" whileInView="visible" viewport={viewportOnce}>Tooling & Stack</motion.h2>
      <motion.p variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="mt-4 max-w-3xl text-white/60 text-sm leading-relaxed">
        A selection of core technologies I’ve used to drive modernization, reliability, and measurable engineering outcomes.
      </motion.p>
      <div
        className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3"
      >
        {[
          { title: 'Frontend & UX', icon: CodeBracketIcon, badge: 'TypeScript+React', points: ['React 18', 'TypeScript', 'Tailwind CSS', 'ShadCN', 'TanStack/ReactRouter'] },
          { title: 'Architecture & Services', icon: CubeTransparentIcon, badge: 'Scale', points: ['Node.js APIs', 'PHP APIs and Services', 'Lambda Microservices', 'Queues & workers', 'Event flows']},
          { title: 'Data & Storage', icon: ServerStackIcon, points: ['PostgreSQL', 'Oracle', 'DynamoDB', 'Redshift', 'S3', 'Query optimization'] },
          { title: 'Cloud & Delivery', icon: CloudIcon, points: ['AWS (S3, CloudFront, Lambda, SNS, SQS)', 'GCP exposure', 'CI/CD pipelines', 'Caching strategy'] },
          { title: 'Observability & Quality', icon: ChartBarSquareIcon, points: ['Structured logging', 'Metrics & traces', 'Performance profiling'] },
          { title: 'Privacy & Governance', icon: ShieldCheckIcon, points: ['Retention policies', 'Deletion workflows', 'GDPR tooling', 'Compliance instrumentation'] },
          { title: 'AI & Experimentation', icon: SparklesIcon, points: ['Transcription-driven features', 'AI titles & summaries', 'Github Copilot', 'Agent Development'] },
          { title: 'Enablement & Scale', icon: UserGroupIcon, badge: 'Tech Leader', points: ['DX automation', 'CI acceleration', 'Mentorship', 'Process refinement', 'Knowledge sharing'] },
  ].map(card => (
          <div
            key={card.title}
            className="group relative rounded-xl border border-white/10 bg-gradient-to-b from-white/5 to-white/[0.02] p-5 backdrop-blur transition hover:border-brand-400/40"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                {card.icon && <card.icon className="h-5 w-5 text-brand-300" aria-hidden="true" />}
                <h3 className="text-base font-semibold text-white tracking-tight">{card.title}</h3>
              </div>
              {card.badge && (
                <span className="shrink-0 rounded-md bg-brand-500/15 text-[10px] font-medium tracking-wide px-2 py-1 text-brand-200 ring-1 ring-inset ring-brand-400/30">
                  {card.badge}
                </span>
              )}
            </div>
            <ul className="mt-4 space-y-1 text-sm text-white/60">
              {card.points.map(p => <li key={p} className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-400" />{p}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Timeline: React.FC = () => (
  <section id="timeline" className="py-28">
    <div className="mx-auto max-w-4xl px-4">
      <motion.h2 variants={fadeIn} initial="hidden" whileInView="visible" viewport={viewportOnce} className="text-3xl font-semibold">Trajectory</motion.h2>
      <div className="mt-12 relative pl-6 border-l border-white/10 space-y-10">
        {[
          { year: '2025', text: 'Building BombBomb 2.0, a SPA with 3x page load times; Building generative AI into our video pipeline with video naming and summaries.' },
          { year: '2024', text: 'React 18 migration across 54 applications; Upgraded video recording to include Video Banners, Beautification, and more. Video pipeline reliability + cost work: 225k recordings/mo at near 0% failure; infra savings realized.' },
          { year: '2023', text: 'Launched a new video editing page with trimming, custom thumbnails, sharing and tracking. Eliminated 39TB of Video Data; Added auto-scaling to Video Pipeline.' },
          { year: '2022', text: 'Re-architected an Electron screen recorder app for improved performance and maintainability. Redesigned our video and email list pages to be more team focused.' },
          { year: '2021', text: 'Improved member management with a redesigned UX; streamlined onboarding and account management processes. Built a cookie identification system for identifying engagement. Built a SCIM implementation for user provisioning.' },
          { year: '2020', text: 'React 16 migration completed. Re-architectured our team management infrastructure for improved scalability and maintainability.' },
          { year: '2019', text: 'Added Single Sign-On (SSO) support for improved user authentication and security. Built a lambda service for video reactions (Likes, Comments). Built a module and component React library for shared use across applications.' },
          { year: '2015–2018', text: 'Scaling video email campaigns & platform foundations at BombBomb.' },
          { year: '2014', text: 'Co-founded Local Sage, a startup developing a web-based app providing public and private data to business owners for optimal location selection. Awarded third place and $10,000 in a statewide competition (sponsored by Google).' },
          { year: '2013–2015', text: 'GeoData & digital marketplace engineering at Sanborn Map Company. Maintained a web application used by LiDAR and photo acquisition field operators. Developed the GeoData Cache website for a state government office and designed the Sanborn Online digital marketplace.' },
          { year: '2003–2013', text: 'Secure systems & cryptography tooling at Lockheed Martin; multiple performance awards.' },
          { year: '2011', text: 'Graduated from Colorado Technical University with a Master of Engineering in Systems Engineering.' },
          { year: '2007', text: 'Graduated from University of Colorado in Colorado Springs with a Bachelor of Science in Computer Science and Minors in Mathematics and Video Game Development.' }
        ].map(t => (
          <motion.div key={t.year} variants={slideInLeft} initial="hidden" whileInView="visible" viewport={viewportOnce} className="relative">
            <span className="absolute -left-[13px] top-2 h-3 w-3 rounded-full bg-brand-400 shadow-glow" />
            <div className="text-sm font-semibold text-brand-300">{t.year}</div>
            <p className="mt-1 text-white/70 leading-relaxed text-sm max-w-prose">{t.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const CallToAction: React.FC = () => (
  <section id="cta" className="py-32">
    <div className="mx-auto max-w-4xl px-4 text-center">
      <motion.h2 variants={revealMask} initial="hidden" whileInView="visible" viewport={viewportOnce} className="text-4xl font-bold tracking-tight">Build with Purpose</motion.h2>
      <motion.p variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="mt-6 text-white/70 max-w-2xl mx-auto leading-relaxed">
        I collaborate on systems architecture, performance audits, and design–engineering bridges. If the work advances clarity, resilience, or user experience, it has my attention.
      </motion.p>
      <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="mt-10 flex flex-wrap justify-center gap-4">
        <a href="/contact" className="btn">Get in Touch</a>
        <a href="#experiments" className="btn-outline">View Experiments</a>
      </motion.div>
    </div>
  </section>
);

export const ExperimentsPreview: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <section id="experiments" className="py-28 border-t border-white/5">
    <div className="mx-auto max-w-7xl px-4">
      <motion.h2 variants={fadeIn} initial="hidden" whileInView="visible" viewport={viewportOnce} className="text-3xl font-semibold">Latest Experiments</motion.h2>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        {children}
      </motion.div>
    </div>
  </section>
);
